import { FunctionComponent, memo, useMemo } from "react";

import { IProps } from "./performance-indicator.types";
import { calculatePercentageFromMean } from "./performance-indicator.utilities";
import classNames from "classnames";
import "./performance-indicator.scss";

/**
 * Indicate the performance of an agent by styling the cell to be red if performance is poor and green if performance
 * is good. Also, optionally, show or hide the percentage difference from the mean.
 * @param props.children       The cell that will be displayed in the table.
 * @param props.negativeIsGood A boolean indicating if a negative difference from the mean is good.
 * @param props.overallValue   The overall value for all interactions.
 * @param props.showPercentage A boolean indicating if the percentage from the mean should be shown.
 * @param props.value          The value being displayed.
 * @returns
 */
const PerformanceIndicator: FunctionComponent<IProps> = ({
    children,
    negativeIsGood = false,
    overallValue,
    showPercentage,
    value,
}) => {
    const percentageFromMean = useMemo(() => {
        return calculatePercentageFromMean(overallValue ?? 0, value ?? 0);
    }, [overallValue, value]);

    const className = classNames({
        "performance-indicator--positive":
            (percentageFromMean >= 5 && !negativeIsGood) || (percentageFromMean <= -5 && negativeIsGood),
        "performance-indicator--negative":
            (percentageFromMean < -5 && !negativeIsGood) || (percentageFromMean > 5 && negativeIsGood),
    });

    return (
        <div className={classNames("performance-indicator", { [className]: !showPercentage })}>
            {children}
            {showPercentage && typeof value !== "undefined" && value !== null && (
                <div>
                    (
                    <span className={className}>{`${
                        percentageFromMean >= 0 ? "+" : ""
                    }${percentageFromMean.toLocaleString(undefined, { maximumFractionDigits: 0 })}%`}</span>
                    )
                </div>
            )}
        </div>
    );
};

export default memo(PerformanceIndicator);
